import { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { IoPersonCircleOutline } from 'react-icons/io5';
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, onSnapshot } from "firebase/firestore";
import { Divider } from 'antd';
import { PiWalletBold, PiChalkboardTeacherBold, PiChatTeardropTextBold, PiBriefcaseBold, PiHeadsetBold, PiPowerBold, PiSparkle } from "react-icons/pi";
import '../webapp.css';
import { ConfirmationModal, AlertStack } from '../functions/ui';
import TutorialModal from './tutorial';
import FeedbackModal from './feedback';
import { addRippleEffect } from '../functions/classes';
import Chat from './chat';

const Profile = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showChatPanel, setShowChatPanel] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        const db = getFirestore();
        const companyDocRef = doc(db, "companyUsers", user.uid);
        const unsubscribeFirestore = onSnapshot(companyDocRef, (docSnap) => {
          if (docSnap.exists()) {
          }
          setLoading(false);
        });
        return () => unsubscribeFirestore();
      } else {
        setLoading(false);
      }
    });
    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    }
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  // Add/remove chat-panel-open class on dash-main-box when chat is open
  useEffect(() => {
    const dashMain = document.querySelector('.dash-main-box');
    if (dashMain) {
      if (showChatPanel) {
        dashMain.classList.add('chat-panel-open');
      } else {
        dashMain.classList.remove('chat-panel-open');
      }
    }
    return () => {
      if (dashMain) dashMain.classList.remove('chat-panel-open');
    };
  }, [showChatPanel]);

  const confirmLogout = async () => {
    try {
      await signOut(getAuth());
      setAlerts(prev => [
        ...prev,
        { id: Date.now() + Math.random(), type: 'success', message: 'Logged out successfully' },
      ]);
      setTimeout(() => { window.location.href = "/login"; }, 500);
    } catch (error) {
      setAlerts(prev => [
        ...prev,
        { id: Date.now() + Math.random(), type: 'error', message: 'Error logging out' },
      ]);
      console.error("Error", error);
    }
    setShowLogoutConfirm(false);
  };

  const cancelLogout = () => setShowLogoutConfirm(false);

  const handleProfileClick = () => {
    setShowLogoutConfirm(false); 
    setIsMenuOpen(open => !open);
  };


  return (
    <nav className="profile-box">
      <PiSparkle size={25} className="profile-icon" onClick={() => setShowChatPanel(true)} />
      <IoPersonCircleOutline className="profile-icon" onClick={handleProfileClick} />
      {isMenuOpen && !showLogoutConfirm && ReactDOM.createPortal(
        <div className="menu-box profile-menu-box" ref={menuRef}>
          {loading ? (
            <div><span>Loading...</span></div>
          ) : (
            <>
              <div className='menu-box-div' onClick={e => { addRippleEffect(e); window.open('https://billing.stripe.com/p/login/14A6oIfpvfbr6L6dSW6J200', '_blank'); }}><PiWalletBold className="profile-menu-icon" /><span>Payments & Plan</span></div>
              <div className='menu-box-div' onClick={e => { addRippleEffect(e); setShowTutorial(true); }}><PiChalkboardTeacherBold className="profile-menu-icon" /><span>Tutorial</span></div>
              <div className='menu-box-div' onClick={e => { addRippleEffect(e); setShowFeedbackModal(true); }}><PiChatTeardropTextBold className="profile-menu-icon" /><span>Feedback</span></div>
              <div className='menu-box-div' onClick={e => { addRippleEffect(e); window.location.href = 'mailto:ceo@kierep.com'; setTimeout(() => { window.focus(); }, 200); }}>
                <PiHeadsetBold className="profile-menu-icon" /><span>Contact Support</span>
              </div>
              <div className='menu-divider-div'><Divider className='menu-divider'/></div>
              <div className='menu-box-div' onClick={e => { addRippleEffect(e); window.open('/privacy', '_blank'); }}><PiBriefcaseBold className="profile-menu-icon" /><span>Policies</span></div>
              <div className='menu-box-div' onClick={e => { addRippleEffect(e); setShowLogoutConfirm(true); }} ><PiPowerBold className="profile-menu-icon" /><span>Log Out</span></div>
            </>
          )}
        </div>,
        document.body
      )}
      {showTutorial && ReactDOM.createPortal(
        <TutorialModal onClose={() => setShowTutorial(false)} />,
        document.body
      )}
      <ConfirmationModal
        open={showLogoutConfirm}
        title="Confirm Logout"
        message="Are you sure you want to log out?"
        confirmText="Log out"
        cancelText="Cancel"
        onConfirm={confirmLogout}
        onCancel={cancelLogout}
        isSubmitting={loading}
      />
      <AlertStack
        alerts={alerts}
        onAlertClose={id => setAlerts(alerts => alerts.filter(a => a.id !== id))}
      />
      {showChatPanel && ReactDOM.createPortal(
        <Chat onClose={() => setShowChatPanel(false)} />,
        document.body
      )}
      {showFeedbackModal && ReactDOM.createPortal(
        <FeedbackModal onClose={() => setShowFeedbackModal(false)} />,
        document.body
      )}
    </nav>
  );
};

export default Profile;